import { customElement, property } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import { install as installTrap, type FocusTrap } from '../../lib/focus-trap';
import { lock as lockScroll, unlock as unlockScroll } from '../../lib/scroll-lock';
import './ext-modal.css';

export type ExtModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';

/**
 * <ext-modal open size="md">
 *   <div slot="header">Titulo</div>
 *   Body del modal
 *   <div slot="footer">
 *     <ext-button>Cerrar</ext-button>
 *   </div>
 * </ext-modal>
 *
 * Estrategia Light DOM: "host es modal" - el propio <ext-modal> se
 * posiciona fixed full viewport y contiene un overlay + dialog. NO usamos
 * portal pattern (el host no se mueve a document.body). El contenido del
 * user (header/body/footer slots) se reordena UNA vez en wrappers internos:
 *   .ext-modal__overlay
 *   .ext-modal__dialog
 *     .ext-modal__header
 *     .ext-modal__body
 *     .ext-modal__footer
 *
 * Visibilidad: controlada por attribute reflected `open`. Oculto por CSS
 * (display:none) cuando !open.
 *
 * Props:
 *  - open: boolean (default false) - controlado por parent.
 *  - size: sm | md | lg | xl | fullscreen (default md).
 *  - closeOnEsc: boolean (default true).
 *  - closeOnBackdrop: boolean (default true).
 *  - persistent: boolean (default false) - desactiva esc y backdrop.
 *  - label: string opcional - aria-label del dialog.
 *
 * Eventos:
 *  - ext:open - al pasar de cerrado a abierto.
 *  - ext:close - al pedir cerrar (esc, backdrop, X). Parent decide si setea open=false.
 */
@customElement('ext-modal')
export class ExtModal extends ExtBaseElement {
  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: String, reflect: true }) size: ExtModalSize = 'md';
  // Defaults `true` para UX estandar de modales (esc/click-out cierran).
  // Modales con datos del user NO deben cerrarse asi → declarar
  // `close-on-esc="false"` y `close-on-backdrop="false"` explicito en el
  // markup (ej. cash-quick-move-modal, cash-close-modal, cash-open-modal).
  // Para readonly-only modals (preview de factura, etc.) dejar los defaults.
  @property({ type: Boolean, attribute: 'close-on-esc' }) closeOnEsc = true;
  @property({ type: Boolean, attribute: 'close-on-backdrop' }) closeOnBackdrop = true;
  @property({ type: Boolean, reflect: true }) persistent = false;
  @property({ type: String }) label = '';

  private _structured = false;
  private _dialogEl: HTMLDivElement | null = null;
  private _focusTrap: FocusTrap | null = null;
  private _isOpen = false; // estado real despues de activar (evita doble lock)

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  render(): null {
    return null;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._structure();
    if (this.open) this._activate();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._isOpen) this._deactivate();
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('open')) {
      if (this.open && !this._isOpen) {
        this._activate();
      } else if (!this.open && this._isOpen) {
        this._deactivate();
      }
    }
    if (changed.has('label') && this._dialogEl) {
      if (this.label) this._dialogEl.setAttribute('aria-label', this.label);
      else this._dialogEl.removeAttribute('aria-label');
    }
  }

  /**
   * Reordena los children originales del host en overlay + dialog(header/body/footer).
   * Idempotente: corre una sola vez.
   */
  private _structure(): void {
    if (this._structured) return;
    const children = Array.from(this.childNodes);
    const headerNodes: Node[] = [];
    const bodyNodes: Node[] = [];
    const footerNodes: Node[] = [];

    for (const node of children) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const slot = (node as Element).getAttribute('slot');
        if (slot === 'header') {
          headerNodes.push(node);
          continue;
        }
        if (slot === 'footer') {
          footerNodes.push(node);
          continue;
        }
      }
      bodyNodes.push(node);
    }

    this.innerHTML = '';

    const overlay = document.createElement('div');
    overlay.className = 'ext-modal__overlay';
    overlay.addEventListener('mousedown', this._onBackdropMouseDown);

    const dialog = document.createElement('div');
    dialog.className = 'ext-modal__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    if (this.label) dialog.setAttribute('aria-label', this.label);
    // Stop propagation en mousedown del dialog para que el backdrop no dispare.
    dialog.addEventListener('mousedown', (ev) => ev.stopPropagation());
    this._dialogEl = dialog;

    if (headerNodes.length > 0) {
      const h = document.createElement('div');
      h.className = 'ext-modal__header';
      headerNodes.forEach((n) => h.appendChild(n));
      dialog.appendChild(h);
    }

    const b = document.createElement('div');
    b.className = 'ext-modal__body';
    bodyNodes.forEach((n) => b.appendChild(n));
    dialog.appendChild(b);

    if (footerNodes.length > 0) {
      const f = document.createElement('div');
      f.className = 'ext-modal__footer';
      footerNodes.forEach((n) => f.appendChild(n));
      dialog.appendChild(f);
    }

    this.appendChild(overlay);
    this.appendChild(dialog);

    this._structured = true;
  }

  private _onBackdropMouseDown = (_ev: MouseEvent): void => {
    if (this.persistent) return;
    if (!this.closeOnBackdrop) return;
    this._requestClose();
  };

  private _onDocumentKeyDown = (ev: KeyboardEvent): void => {
    if (ev.key !== 'Escape') return;
    if (this.persistent) return;
    if (!this.closeOnEsc) return;
    ev.stopPropagation();
    ev.preventDefault();
    this._requestClose();
  };

  private _requestClose(): void {
    this._emit('close');
  }

  private _activate(): void {
    if (this._isOpen) return;
    this._isOpen = true;
    lockScroll();
    document.addEventListener('keydown', this._onDocumentKeyDown, true);
    if (this._dialogEl) {
      this._focusTrap = installTrap(this._dialogEl);
    }
    this._emit('open');
  }

  private _deactivate(): void {
    if (!this._isOpen) return;
    this._isOpen = false;
    document.removeEventListener('keydown', this._onDocumentKeyDown, true);
    if (this._focusTrap) {
      this._focusTrap.uninstall();
      this._focusTrap = null;
    }
    unlockScroll();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-modal': ExtModal;
  }
}
